import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { INSUFFICIENT_CREDITS_EVENT } from '../api/config';
import TopUpSheet from './TopUpSheet';
import { useUser } from '../contexts/UserContext';

const FEATURE_LABELS: Record<string, string> = {
  basic_skin_scan: 'This scan',
  product_barcode: 'A product scan',
  product_image: 'A product scan',
  premium_face: 'A premium analysis',
  hair_analysis: 'A hair analysis',
  makeup_analysis: 'A makeup analysis',
};

interface InsufficientCreditsPayload {
  feature_key?: string;
  feature?: string;
  details?: { feature_key?: string };
  required?: number;
  cost?: number;
  credit_cost?: number;
}

interface InsufficientCreditsDetail {
  path?: string;
  payload?: InsufficientCreditsPayload;
}

/**
 * Global listener that turns any API 402 ("insufficient credits") into the
 * credit top-up popup. Mounted once, near the app root, inside the user
 * provider so it can read/refresh balances.
 *
 * Only one sheet is shown at a time; while it's open, further 402s are ignored
 * (they'd be the same failing action retried).
 */
export default function InsufficientCreditsHost(): React.JSX.Element | null {
  const { refreshCredits } = useUser();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<{ label?: string | null; cost?: number | null }>({});

  useEffect(() => {
    const onEvent = (e: Event) => {
      const ce = e as CustomEvent<InsufficientCreditsDetail>;
      const payload = ce.detail.payload;
      const featureKey =
        payload?.feature_key ?? payload?.feature ?? payload?.details?.feature_key;
      const cost =
        (typeof payload?.required === 'number' ? payload.required : null) ??
        (typeof payload?.cost === 'number' ? payload.cost : null) ??
        (typeof payload?.credit_cost === 'number' ? payload.credit_cost : null);
      setDetail({
        label: featureKey ? FEATURE_LABELS[featureKey] ?? 'This action' : null,
        cost,
      });
      setOpen(true);
    };
    window.addEventListener(INSUFFICIENT_CREDITS_EVENT, onEvent);
    return () => { window.removeEventListener(INSUFFICIENT_CREDITS_EVENT, onEvent); };
  }, []);

  const close = () => {
    setOpen(false);
    void refreshCredits();
  };

  return (
    <AnimatePresence>
      {open && (
        <TopUpSheet
          key="insufficient-credits-sheet"
          featureLabel={detail.label}
          featureCost={detail.cost}
          onClose={close}
          onPurchased={() => { void refreshCredits(); }}
        />
      )}
    </AnimatePresence>
  );
}
